import * as React from 'react'
import '@fontsource-variable/archivo/wdth.css'
import '@fontsource/ibm-plex-sans/400.css'
import '@fontsource/ibm-plex-sans/600.css'
import '@fontsource/ibm-plex-mono/500.css'
import type {
  AcceptanceBlock,
  CoverBlock,
  FactsBlock,
  IrrigationFindingsBlock,
  SinceLastVisitBlock,
  FutureWorkBlock,
  IrrigationScheduleBlock,
  JobLegendBlock,
  PhotosBlock,
  PricingBlock,
  ProblemsBlock,
  ProposalDocumentDto,
  ProseBlock,
  ProseRole,
  SiteMapBlock,
  SummaryBlock,
  TreeScheduleBlock,
} from '@lib/dto/proposal-document.dto'
import {
  PROPOSAL_CHAPTER_LABELS,
  proposalChapterSchema,
  type ProposalChapter,
} from '@lib/enums/proposal-chapter.enum'
import { EMPTY_SELECTION, documentTaxRate, priceSelection } from '@lib/helpers/proposal-selection'
import { TREE_PRIORITIES, planFigures } from '@lib/helpers/proposal-plans'
import './document.css'
import { cn } from '@/lib/utils'
import type { Choosing } from '@/proposal-document/choosing'
import { PhotoSourceProvider, type PhotoSource } from '@/proposal-document/Photo'
import { useChapterDwell, useTrack, type Track } from '@/proposal-document/activity'
import { SiteExplorer, type ExplorerHighlight } from '@/proposal-document/SiteExplorer'
import { SitePlanSvg } from '@/proposal-document/SitePlanSvg'
import { PRIORITY_LEGEND, priorityClass } from '@/proposal-document/canopy'
import { PhoneBar, PlanChapter, PlanRail } from '@/proposal-document/PlanChapter'
import {
  Appendix,
  BandTrio,
  Chapter,
  Closing,
  FieldPhotos,
  Findings,
  IrrigationFindings,
  SinceLastVisit,
  FutureWork,
  Hero,
  HowItGoes,
  NextSteps,
  PrintCover,
  ShortVersion,
  Trust,
  recommendedOf,
  type AcceptControls,
  type FindingTarget,
} from '@/proposal-document/chapters'

/**
 * The whole proposal, as a client reads it.
 *
 * Presentational: it takes a resolved document and lays it out. What a
 * proposal SAYS is decided on the server (the template and the survey); what
 * this file decides is where each part goes, which is why it places blocks by
 * what they are rather than by their order in the list. That is also what lets
 * a proposal sent before this layout existed open in it.
 *
 * The order answers a board's questions as they come: whose property, the
 * gist and the price, the evidence, what was found, why this bid, the plan,
 * how the work goes, what happens next. Lines of text stay at reading length;
 * a wide screen spends its width on the plan and the photos, a chapter list on
 * the left and the plan beside the page on the right, so the total and the
 * Accept button are always in view.
 */

interface Parts {
  cover: CoverBlock | null
  summary: SummaryBlock | null
  maps: SiteMapBlock[]
  schedules: TreeScheduleBlock[]
  legend: JobLegendBlock | null
  problems: ProblemsBlock | null
  irrigationSchedules: IrrigationScheduleBlock[]
  irrigationFindings: IrrigationFindingsBlock | null
  /** "Since our last visit", one per site visited before. */
  sinceLastVisit: SinceLastVisitBlock[]
  /** Work recommended for later seasons, when there is any. */
  futureWork: FutureWorkBlock | null
  photos: PhotosBlock | null
  facts: FactsBlock | null
  pricing: PricingBlock | null
  acceptance: AcceptanceBlock | null
  prose: Partial<Record<ProseRole, ProseBlock>>
  /** Prose from an older document that no role fits: shown with the answers. */
  otherProse: ProseBlock[]
}

/**
 * A role for a prose title frozen before roles existed.
 *
 * The title is free text. Only these five ever meant a role; anything else
 * is prose the page does not know where to put.
 */
function legacyRole(title: string | null): ProseRole | null {
  switch (title) {
    case 'What is not included':
      return 'exclusions'
    case 'Access, scheduling and residents':
      return 'access'
    case 'Site preparation and safety':
      return 'preparation'
    case 'How the work is performed':
      return 'standards'
    case 'Terms':
      return 'terms'
    default:
      return null
  }
}

function partsOf(document: ProposalDocumentDto): Parts {
  const parts: Parts = {
    cover: null,
    summary: null,
    maps: [],
    schedules: [],
    legend: null,
    problems: null,
    irrigationSchedules: [],
    irrigationFindings: null,
    sinceLastVisit: [],
    futureWork: null,
    photos: null,
    facts: null,
    pricing: null,
    acceptance: null,
    prose: {},
    otherProse: [],
  }
  let sawNarrative = false
  for (const block of document.blocks) {
    switch (block.type) {
      case 'cover':
        parts.cover = block
        break
      case 'summary':
        parts.summary = block
        break
      case 'siteMap':
        parts.maps.push(block)
        break
      case 'treeSchedule':
        parts.schedules.push(block)
        break
      case 'jobLegend':
        parts.legend = block
        break
      case 'problems':
        parts.problems = block
        break
      case 'irrigationSchedule':
        parts.irrigationSchedules.push(block)
        break
      case 'irrigationFindings':
        parts.irrigationFindings = block
        break
      case 'sinceLastVisit':
        parts.sinceLastVisit.push(block)
        break
      case 'futureWork':
        parts.futureWork = block
        break
      case 'photos':
        parts.photos = block
        break
      case 'facts':
        parts.facts = block
        break
      case 'pricing':
        parts.pricing = block
        break
      case 'acceptance':
        parts.acceptance = block
        break
      case 'prose': {
        const role: ProseRole | null =
          block.role ?? (sawNarrative ? legacyRole(block.title) : 'narrative')
        if (role === 'narrative') sawNarrative = true
        if (role === null) parts.otherProse.push(block)
        else parts.prose[role] = block
        break
      }
    }
  }
  return parts
}

interface ChapterLink {
  id: ProposalChapter
  label: string
}

function chapterLink(id: ProposalChapter): ChapterLink {
  return { id, label: PROPOSAL_CHAPTER_LABELS[id] }
}

/** The chapter list on the left of a wide screen, with the one being read. */
/** The line under the findings chapter's title, for what it holds. */
function findingsLede(hasTrees: boolean, hasIrrigation: boolean): string {
  if (!hasTrees && !hasIrrigation) {
    return 'What we found again, and what has changed since our last visit.'
  }
  if (!hasIrrigation) {
    return 'Grouped by kind of problem, each with the trees it affects and what we would do about it.'
  }
  if (!hasTrees) return 'Grouped by kind of repair, each with how many we found and where.'
  return 'Grouped by kind: the trees, each with what we would do about it, then the irrigation, each with how many we found and where.'
}

/** The line under the plan chapter's title, for what the reader can do there. */
function planLede(canChoose: boolean, hasPlans: boolean): string | undefined {
  if (!canChoose) return undefined
  return hasPlans
    ? 'Start from a plan. Adjust it if you need to: every change is listed, with what it is worth.'
    : 'Leave out anything you need to: every change is listed, with what it is worth.'
}

function ChapterNav({
  chapters,
  active,
}: {
  chapters: ChapterLink[]
  active: ProposalChapter | null
}) {
  return (
    <nav className="chapters" aria-label="Chapters">
      <p className="chapters-count proposal-mono">
        {active === null
          ? `${String(chapters.length)} chapters`
          : `Chapter ${String(chapters.findIndex((chapter) => chapter.id === active) + 1)} of ${String(chapters.length)}`}
      </p>
      <ol>
        {chapters.map((chapter) => (
          <li key={chapter.id}>
            <a href={`#${chapter.id}`} aria-current={active === chapter.id ? 'true' : undefined}>
              {chapter.label}
            </a>
          </li>
        ))}
      </ol>
    </nav>
  )
}

/** Which chapter is in view, for the chapter list. */
function useActiveChapter(ids: readonly ProposalChapter[]): ProposalChapter | null {
  const [active, setActive] = React.useState<ProposalChapter | null>(null)
  // A new array every render. The joined key is only the effect's dependency;
  // each piece is parsed back into a chapter before it is used.
  const key = ids.join('|')
  React.useEffect(() => {
    if (typeof IntersectionObserver === 'undefined') return
    const chapters: ProposalChapter[] = []
    for (const part of key.split('|')) {
      const parsed = proposalChapterSchema.safeParse(part)
      if (parsed.success) chapters.push(parsed.data)
    }
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top)[0]
        const parsed = proposalChapterSchema.safeParse(
          visible?.target.getAttribute('data-chapter'),
        )
        if (parsed.success) setActive(parsed.data)
      },
      { rootMargin: '-20% 0px -65% 0px' },
    )
    for (const id of chapters) {
      const element = window.document.getElementById(id)
      if (element !== null) observer.observe(element)
    }
    return () => {
      observer.disconnect()
    }
  }, [key])
  return active
}

export interface ProposalDocumentProps {
  document: ProposalDocumentDto
  /** The reader's plan and choices. Null for a wording preview. */
  choosing?: Choosing | null
  /** The shared page's Accept, meeting and share controls. Null elsewhere. */
  accept?: AcceptControls | null
  photoSource?: PhotoSource
  /** Point at trees with no time estimate. Staff preview only. */
  markUnestimated?: boolean
  /** The link the printed copy's QR codes open. Null prints none. */
  shareUrl?: string | null
  /**
   * `preview` drops the parts that belong to a whole page (the phone bar,
   * the rails, the closing band) for the wording editor's dialog.
   */
  variant?: 'page' | 'preview'
  /** The slides are up over the document, so reading time stops. */
  isPresenting?: boolean
}

export function ProposalDocument({
  document,
  choosing = null,
  accept = null,
  photoSource = { kind: 'none' },
  markUnestimated = false,
  shareUrl = null,
  variant = 'page',
  isPresenting = false,
}: ProposalDocumentProps) {
  const parts = React.useMemo(() => partsOf(document), [document])
  const figures = React.useMemo(() => planFigures(document), [document])
  // One plan is no choice: the chapter is then simply the price.
  const hasPlans = figures.filter((figure) => figure.isAvailable).length > 1
  const taxRate = documentTaxRate(document)
  const [highlight, setHighlight] = React.useState<ExplorerHighlight | null>(null)

  const offered = priceSelection(document, EMPTY_SELECTION)
  /*
   * The figure a proposal leads with is the recommendation. With optional
   * work that is CORE, not everything; quoting the larger figure first would
   * make the extras look like part of what we said must be done.
   */
  const recommendedDue =
    figures.find((figure) => figure.plan === recommendedOf(figures))?.amountDue ??
    offered.amountDue
  const cover = parts.cover
  const organization = cover?.preparedByOrganization ?? 'Proposal'
  const phone = cover?.phone ?? null
  const homeCount = parts.summary?.homeCount ?? null
  const firstMap = parts.maps[0] ?? null
  const scheduleFor = (site: string): TreeScheduleBlock | undefined =>
    parts.schedules.find((schedule) => schedule.siteName === site)
  const irrigationFor = (site: string): IrrigationScheduleBlock | undefined =>
    parts.irrigationSchedules.find((schedule) => schedule.siteName === site)

  /*
   * A plan is worth showing when it has the trees on it, or repairs pinned.
   * A proposal for irrigation sent before the plan drew repairs has neither,
   * and no plan.
   */
  const shownMaps = parts.maps.filter(
    (map) =>
      document.includesTrees || (map.irrigation?.repairs.length ?? 0) > 0,
  )
  const showsProperty = shownMaps.length > 0
  const property = propertyWords(
    document.includesTrees && shownMaps.some((map) => map.trees.length > 0),
    shownMaps.some((map) => (map.irrigation?.repairs.length ?? 0) > 0),
  )
  const showsFindings =
    parts.problems !== null ||
    parts.irrigationFindings !== null ||
    parts.sinceLastVisit.length > 0

  const how = [
    parts.prose.access,
    parts.prose.preparation,
    parts.prose.standards,
    ...parts.otherProse,
  ].filter((section): section is ProseBlock => section !== undefined)

  const chapters: ChapterLink[] = [
    chapterLink('short'),
    ...(showsProperty ? [chapterLink('property')] : []),
    ...(showsFindings ? [chapterLink('found')] : []),
    chapterLink('why'),
    ...(parts.pricing === null ? [] : [chapterLink('plan')]),
    ...(parts.futureWork === null ? [] : [chapterLink('later')]),
    ...(how.length > 0 ? [chapterLink('how')] : []),
    chapterLink('next'),
    chapterLink('appendix'),
  ]
  const active = useActiveChapter(chapters.map((chapter) => chapter.id))
  const track = useTrack()
  useChapterDwell(active, track, isPresenting)
  useTrackChoices(choosing, track)

  /*
   * Printed onto the margin of every page. A custom property, because a
   * margin box cannot read an element. Cleared on unmount so another page's
   * print does not keep the name.
   */
  React.useEffect(() => {
    const root = window.document.documentElement
    const footer = phone === null ? organization : `${organization} · ${phone}`
    root.style.setProperty('--proposal-org', JSON.stringify(footer.toLocaleUpperCase('en-US')))
    return () => {
      root.style.removeProperty('--proposal-org')
    }
  }, [organization, phone])

  function showOnPlan(name: string, target: FindingTarget): void {
    if (target.kind === 'repairs') {
      const repairs = target.repairKeys.size
      setHighlight({
        kind: 'repairs',
        site: target.site,
        repairKeys: target.repairKeys,
        description:
          repairs === 1
            ? `the repair for ${name}`
            : `the ${String(repairs)} repairs for ${name}`,
      })
    } else {
      setHighlight({
        kind: 'trees',
        site: target.site,
        labels: target.labels,
        description:
          target.labels.size === 1
            ? `the tree with ${name}`
            : `the ${String(target.labels.size)} trees with ${name}`,
      })
    }
    window.document.getElementById('property')?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const isPage = variant === 'page'

  return (
    <PhotoSourceProvider source={photoSource}>
      <div className={cn('proposal-shell', `proposal-shell--${variant}`)}>
        {cover === null ? null : (
          <>
            <BandTrio className="proposal-band--top" />
            <Hero
              cover={cover}
              summary={parts.summary}
              firstSite={firstMap}
              firstSchedule={firstMap === null ? null : (scheduleFor(firstMap.siteName) ?? null)}
              firstIrrigation={firstMap === null ? null : (irrigationFor(firstMap.siteName) ?? null)}
              figures={figures}
              amountDue={recommendedDue}
            />
            <PrintCover
              cover={cover}
              firstSite={firstMap}
              firstSchedule={firstMap === null ? null : (scheduleFor(firstMap.siteName) ?? null)}
              firstIrrigation={firstMap === null ? null : (irrigationFor(firstMap.siteName) ?? null)}
              shareUrl={shareUrl}
            />
          </>
        )}

        <div className="proposal-layout">
          {isPage ? <ChapterNav chapters={chapters} active={active} /> : null}

          <main className="proposal-main">
            <ShortVersion
              narrative={parts.prose.narrative ?? null}
              summary={parts.summary}
              figures={figures}
              amountDue={recommendedDue}
              hasChoosing={choosing !== null}
            />

            {!showsProperty ? null : (
              <Chapter
                id="property"
                kicker="Your property"
                title={property.title}
                lede={property.lede}
                screenOnly
                wide
              >
                {shownMaps.map((map) => (
                  <div key={map.siteName} className="property-site">
                    <div className="print:hidden">
                      <SiteExplorer
                        map={map}
                        schedule={scheduleFor(map.siteName)}
                        irrigation={irrigationFor(map.siteName)}
                        irrigationFindings={parts.irrigationFindings}
                        legend={parts.legend}
                        choosing={choosing}
                        highlight={highlight}
                        onClearHighlight={() => {
                          setHighlight(null)
                        }}
                        markUnestimated={markUnestimated}
                        phone={phone}
                        showSiteName={shownMaps.length > 1}
                      />
                    </div>
                    <PrintPlan
                      map={map}
                      schedule={scheduleFor(map.siteName)}
                      irrigation={irrigationFor(map.siteName)}
                      choosing={choosing}
                      showSiteName={shownMaps.length > 1}
                    />
                  </div>
                ))}
              </Chapter>
            )}

            {!showsFindings ? null : (
              <Chapter
                id="found"
                kicker="What we found"
                title="What we found, worst first"
                lede={findingsLede(parts.problems !== null, parts.irrigationFindings !== null)}
                wide
              >
                {parts.problems === null ? null : (
                  <>
                    {parts.irrigationFindings === null ? null : (
                      <h3 className="proposal-h3">The trees</h3>
                    )}
                    <Findings
                      block={parts.problems}
                      maps={parts.maps}
                      choosing={choosing}
                      onShowOnPlan={showOnPlan}
                    />
                  </>
                )}
                {parts.irrigationFindings === null ? null : (
                  <>
                    {parts.problems === null ? null : (
                      <h3 className="proposal-h3">The irrigation</h3>
                    )}
                    <IrrigationFindings
                      block={parts.irrigationFindings}
                      schedules={parts.irrigationSchedules}
                      maps={shownMaps}
                      choosing={choosing}
                      onShowOnPlan={showOnPlan}
                    />
                  </>
                )}
                {parts.sinceLastVisit.length === 0 ? null : (
                  <SinceLastVisit
                    blocks={parts.sinceLastVisit}
                    showSiteName={parts.sinceLastVisit.length > 1}
                  />
                )}
                {parts.photos === null ? null : (
                  <FieldPhotos block={parts.photos} coverId={cover?.coverPhotoDocumentId ?? null} />
                )}
              </Chapter>
            )}

            {cover === null ? null : (
              <Chapter id="why" kicker="Why us" title={`Why ${organization}`}>
                <Trust
                  cover={cover}
                  facts={parts.facts}
                  reviews={parts.prose.reviews ?? null}
                  comparison={parts.prose.comparison ?? null}
                />
              </Chapter>
            )}

            {parts.pricing === null ? null : (
              <Chapter
                id="plan"
                kicker="Your plan"
                title={choosing !== null && hasPlans ? 'Choose a plan' : 'The price'}
                lede={planLede(choosing !== null, hasPlans)}
                screenOnly
              >
                <PlanChapter
                  document={document}
                  pricing={parts.pricing}
                  offered={offered}
                  choosing={choosing}
                  assurances={parts.prose.assurances ?? null}
                  taxRate={taxRate}
                  homeCount={homeCount}
                />
              </Chapter>
            )}

            {parts.futureWork === null ? null : (
              <Chapter
                id="later"
                kicker="Later seasons"
                title={parts.futureWork.title ?? 'Future work'}
              >
                <FutureWork block={parts.futureWork} />
              </Chapter>
            )}

            {how.length === 0 ? null : (
              <Chapter
                id="how"
                kicker="How the work goes"
                title="What happens on the property"
                lede="Residents, parking, safety and cleanup. Open any answer."
                screenOnly
              >
                <HowItGoes sections={how} />
              </Chapter>
            )}

            <Chapter
              id="next"
              kicker="Next steps"
              title={parts.prose.nextSteps?.title ?? 'What happens after you accept'}
            >
              <NextSteps
                steps={parts.prose.nextSteps ?? null}
                acceptance={parts.acceptance}
                choosing={choosing}
                accept={accept}
                taxRate={taxRate}
                figures={figures}
                shareUrl={shareUrl}
                phone={phone}
              />
            </Chapter>

            <Chapter id="appendix" kicker="Appendix" title="The details">
              <Appendix
                schedules={parts.schedules}
                irrigationSchedules={parts.irrigationSchedules}
                legend={parts.legend}
                exclusions={parts.prose.exclusions ?? null}
                terms={parts.prose.terms ?? null}
                markUnestimated={markUnestimated}
              />
            </Chapter>
          </main>

          {isPage ? (
            <div className="proposal-rail-column">
              <PlanRail
                choosing={choosing}
                pricing={parts.pricing}
                offered={offered}
                homeCount={homeCount}
                validUntil={cover?.validUntil ?? null}
                phone={phone}
                canAccept={accept !== null && accept.accepted === null}
              />
            </div>
          ) : null}
        </div>

        {isPage ? <Closing organization={organization} phone={phone} /> : null}
        {isPage ? (
          <PhoneBar
            choosing={choosing}
            pricing={parts.pricing}
            offered={offered}
            canAccept={accept !== null && accept.accepted === null}
          />
        ) : null}
      </div>
    </PhotoSourceProvider>
  )
}

/**
 * Report the reader's decisions as they make them: each plan they switch to,
 * and every change they add. Undoing is not
 * reported; trying something and taking it back is what the list already
 * shows by its absence.
 */
function useTrackChoices(choosing: Choosing | null, track: Track): void {
  const plan = choosing?.state.plan ?? null
  const texts = (choosing?.changes ?? []).map((change) => change.text).join('\n')
  const first = React.useRef(true)
  const seenPlan = React.useRef<typeof plan>(plan)
  const seenTexts = React.useRef(new Set(texts === '' ? [] : texts.split('\n')))

  React.useEffect(() => {
    if (first.current || plan === null || plan === seenPlan.current) return
    seenPlan.current = plan
    track({ kind: 'PLAN', detail: plan })
  }, [plan, track])

  React.useEffect(() => {
    if (first.current) return
    for (const text of texts === '' ? [] : texts.split('\n')) {
      if (seenTexts.current.has(text)) continue
      seenTexts.current.add(text)
      track({ kind: 'ADJUSTED', detail: text })
    }
  }, [texts, track])

  React.useEffect(() => {
    first.current = false
  }, [])
}

/** The plan as it prints: the still, its legend, and nothing to click. */
function PrintPlan({
  map,
  schedule,
  irrigation,
  choosing,
  showSiteName,
}: {
  map: SiteMapBlock
  schedule: TreeScheduleBlock | undefined
  irrigation: IrrigationScheduleBlock | undefined
  choosing: Choosing | null
  showSiteName: boolean
}) {
  const rows = schedule?.rows ?? []
  const hasRepairs = (map.irrigation?.repairs.length ?? 0) > 0
  return (
    <div className="print-plan" aria-hidden="true">
      {showSiteName ? <h3 className="proposal-h3">{map.siteName}</h3> : null}
      <SitePlanSvg
        block={map}
        rows={rows}
        aspectMax={1.05}
        statusOf={(label) => {
          const row = rows.find((entry) => entry.label === label)
          return row === undefined || choosing === null
            ? 'in'
            : choosing.treeStatus(map.siteName, row)
        }}
        irrigation={irrigation}
        repairStatusOf={(repair) =>
          choosing === null ? 'in' : choosing.repairStatus(map.siteName, repair)
        }
      />
      <ul className="explorer-legend">
        {TREE_PRIORITIES.map((priority) => (
          <li key={priority}>
            <span className={cn('explorer-swatch', priorityClass(priority))} />
            {PRIORITY_LEGEND[priority]}
          </li>
        ))}
        {hasRepairs ? (
          <li>
            <span className="explorer-swatch explorer-swatch--repair" />
            Irrigation repair
          </li>
        ) : null}
        <li className="explorer-legend-note">{printNote(map.trees.length > 0, hasRepairs)}</li>
      </ul>
    </div>
  )
}

/** The note under the printed plan, for what it shows. */
function printNote(hasTrees: boolean, hasRepairs: boolean): string {
  if (!hasRepairs) return 'Numbers match the schedule in the appendix.'
  if (!hasTrees) return 'R-numbers match the irrigation repairs in the appendix.'
  return 'Circles are trees; tags are irrigation repairs. Numbers match the appendix.'
}

/** The property chapter's heading and lede, for what its plans show. */
function propertyWords(
  hasTrees: boolean,
  hasRepairs: boolean,
): { title: string; lede: string } {
  if (hasTrees && hasRepairs) {
    return {
      title: 'Every tree and repair we recorded, on the plan',
      lede: 'Trees are numbered circles and irrigation repairs are R-numbered tags. Colors show what is urgent. Pick one to see its photos and decide about it.',
    }
  }
  if (hasRepairs) {
    return {
      title: 'Every repair we recorded, on the plan',
      lede: 'Numbered R1, R2 and on, as in the appendix. Colors show what is urgent. Pick a repair to see it and decide about it.',
    }
  }
  return {
    title: 'Every tree we recorded, on the plan',
    lede: 'Numbered on site. Colors show what is urgent; switch to the work, the species or the areas. Pick a tree to see its photos and decide about it.',
  }
}
